import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Button, ButtonAppearance, ButtonSize, Heading, Text, Lozenge } from '@atlassian/learning-components';
import { N0 } from '@atlaskit/theme/colors';

import { formatDateTimeFromISO } from 'src/utils/date';

export type TrainingStatus = 'upcoming' | 'completed';

export interface LiveTrainingCardProps {
  id: string;
  title: string;
  subtitle: string;
  dateTime: string;
  status: TrainingStatus;
  enrolled: boolean;
  product: string;
  label?: string;
  host: string;
  detailsLink: string;
  onDetailsClick?: () => void;
  onAddToCalendar?: () => void;
  onDownloadMaterials?: () => void;
  showAddToCalendar?: boolean;
  showDownloadMaterials?: boolean;
}

export const LiveTrainingCard = ({
  id,
  title,
  subtitle,
  dateTime,
  status,
  enrolled,
  product,
  label,
  host,
  detailsLink,
  onDetailsClick,
  onAddToCalendar,
  onDownloadMaterials,
  showAddToCalendar = false,
  showDownloadMaterials = false,
}: LiveTrainingCardProps) => {
  const handleDetailsClick = (event: React.MouseEvent): void => {
    if (onDetailsClick) {
      event.preventDefault();
      onDetailsClick();
    }
  };

  const handleAddToCalendar = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onAddToCalendar) {
      onAddToCalendar();
    }
  };

  const handleDownloadMaterials = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onDownloadMaterials) {
      onDownloadMaterials();
    }
  };

  const getStatusText = (): string => (status === 'upcoming' ? 'Upcoming' : 'Completed');

  const formattedDateTime = formatDateTimeFromISO(dateTime);

  return (
    <article css={cardContainerStyles} data-testid="live-training-card" id={id}>
      <div css={cardHeaderStyles}>
        <div css={headerTopRowStyles}>
          {label && (
            <div css={labelContainerStyles} data-testid="card-label">
              <Text size="small" color={token('color.text.subtle')}>
                {label}
              </Text>
            </div>
          )}
          <div css={statusContainerStyles}>
            <Lozenge
              text={getStatusText()}
              appearance={status === 'upcoming' ? 'inprogress' : 'success'}
              data-testid="card-status"
            />
          </div>
        </div>
        <div css={titleContainerStyles}>
          <a href={detailsLink} onClick={handleDetailsClick} css={titleLinkStyles} data-testid="card-title-link">
            <Heading level={3} data-testid="card-title">
              {title}
            </Heading>
          </a>
        </div>
        <div css={subtitleContainerStyles}>
          <Text size="regular" color={token('color.text.subtle')} data-testid="card-subtitle">
            {subtitle}
          </Text>
        </div>
      </div>

      <div css={cardBodyStyles}>
        <div css={infoRowStyles}>
          <div css={infoItemStyles}>
            <Text size="small" color={token('color.text.subtlest')}>
              Product
            </Text>
            <Text size="regular" data-testid="card-product">
              {product}
            </Text>
          </div>
          <div css={infoItemStyles}>
            <Text size="small" color={token('color.text.subtlest')}>
              Host
            </Text>
            <Text size="regular" data-testid="card-host">
              {host}
            </Text>
          </div>
        </div>

        <div css={infoRowStyles}>
          <div css={infoItemStyles}>
            <Text size="small" color={token('color.text.subtlest')}>
              Date & Time
            </Text>
            <Text size="regular" data-testid="card-datetime">
              {formattedDateTime}
            </Text>
          </div>
          <div css={infoItemStyles}>
            <Text size="small" color={token('color.text.subtlest')}>
              Enrollment
            </Text>
            <Text size="regular" data-testid="card-enrollment">
              {enrolled ? 'Enrolled' : 'Not enrolled'}
            </Text>
          </div>
        </div>
      </div>

      <div css={cardFooterStyles}>
        <Button
          appearance={ButtonAppearance.LINK}
          label="View course details"
          href={detailsLink}
          onClick={handleDetailsClick}
          data-testid="view-details-button"
        />
        <div css={actionButtonsContainerStyles}>
          {showAddToCalendar && (
            <Button
              appearance={ButtonAppearance.DEFAULT}
              size={ButtonSize.MEDIUM}
              label="Add to calendar"
              onClick={handleAddToCalendar}
              data-testid="add-to-calendar-button"
            />
          )}
          {showDownloadMaterials && (
            <Button
              appearance={ButtonAppearance.DEFAULT}
              size={ButtonSize.MEDIUM}
              label="Download materials"
              onClick={handleDownloadMaterials}
              data-testid="download-materials-button"
            />
          )}
        </div>
      </div>
    </article>
  );
};

const cardContainerStyles = css({
  backgroundColor: N0,
  border: `1px solid ${token('color.border')}`,
  borderRadius: '8px',
  padding: token('space.300'),
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  transition: 'box-shadow 0.2s ease-in-out',
  width: '100%',
  maxWidth: '100%',

  '&:hover': {
    boxShadow: token('elevation.shadow.raised'),
  },

  [media.below.sm]: {
    padding: token('space.250'),
    gap: token('space.150'),
  },
});

const cardHeaderStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
  paddingBottom: token('space.150'),
  borderBottom: `1px solid ${token('color.border')}`,
});

const headerTopRowStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: token('space.050'),
});

const labelContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const statusContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const titleContainerStyles = css({
  marginBottom: token('space.050'),
});

const titleLinkStyles = css({
  textDecoration: 'none',
  color: token('color.text'),

  '&:hover': {
    textDecoration: 'underline',
  },

  h3: {
    margin: 0,
    fontSize: '20px',
    lineHeight: '24px',
    fontWeight: 600,
    color: token('color.text'),

    [media.below.sm]: {
      fontSize: '18px',
      lineHeight: '22px',
    },
  },
});

const subtitleContainerStyles = css({
  display: 'flex',
});

const cardBodyStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.150'),
  paddingTop: token('space.150'),
  paddingBottom: token('space.150'),
});

const infoRowStyles = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: token('space.200'),

  [media.below.sm]: {
    gridTemplateColumns: '1fr',
    gap: token('space.150'),
  },
});

const infoItemStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.050'),
});

const cardFooterStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: token('space.150'),
  borderTop: `1px solid ${token('color.border')}`,
  flexWrap: 'wrap',
  gap: token('space.150'),

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const actionButtonsContainerStyles = css({
  display: 'flex',
  gap: token('space.100'),
  flexWrap: 'wrap',

  [media.below.sm]: {
    width: '100%',
    flexDirection: 'column',

    button: {
      width: '100%',
    },
  },
});
